import { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import Cache from "../utils/cache";

const PAGE_SIZE = 20;

export default function WeeklyAds() {
  const [weeklyAds, setWeeklyAds] = useState([]);
  const [vendorsData, setVendorsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  useEffect(() => {
    async function loadWeeklyAdsWithCache() {
      setLoading(true);
      try {
        const now = new Date();

        const cachedData = Cache.getData();
        let allWeeklyAds = cachedData.weeklyAds || [];
        let cachedVendorsData = cachedData.vendorsData || {};
        const needToFetchAds = !allWeeklyAds.length;

        if (needToFetchAds) {
          const weeklyAdsSnapshot = await getDocs(collection(db, "weekly_ads"));
          allWeeklyAds = weeklyAdsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          // Cache fresh ads
          Cache.setData({
            ...cachedData,
            weeklyAds: allWeeklyAds,
          });
        }

        // Filter ads that are active now and have a vendor
        const filteredWeeklyAds = allWeeklyAds.filter((ad) => {
          const start = ad.startDate?.toDate ? ad.startDate.toDate() : new Date(ad.startDate);
          const end = ad.endDate?.toDate ? ad.endDate.toDate() : new Date(ad.endDate);

          return ad.vendor && start <= now && end >= now;
        });


        filteredWeeklyAds.sort((a, b) =>
          (a.vendor || "").toLowerCase().localeCompare((b.vendor || "").toLowerCase())
        );

        // Extract unique vendor names
        const vendorNames = [...new Set(filteredWeeklyAds.map((ad) => ad.vendor))].filter(Boolean);

        // If cached vendor data has all vendors, use it; else fetch missing vendors
        const vendorsObj = { ...cachedVendorsData };

        const missingVendors = vendorNames.filter((name) => !(name in vendorsObj));

        if (missingVendors.length > 0) {
          // Load all vendor group docs once
          let vendorGroupDocs = [];
          try {
            const vendorsCollectionSnapshot = await getDocs(collection(db, "vendors"));
            vendorGroupDocs = vendorsCollectionSnapshot.docs.filter((doc) =>
              doc.id.startsWith("vendors_")
            );
          } catch (e) {
            console.error("Error loading vendor groups:", e);
          }

          missingVendors.forEach((vendorName) => {
            let foundVendor = null;
            for (const vdoc of vendorGroupDocs) {
              const data = vdoc.data();
              if (Array.isArray(data.vendors)) {
                foundVendor = data.vendors.find(
                  (v) =>
                    (v.vendorName && v.vendorName.toLowerCase() === vendorName.toLowerCase()) ||
                    (v.id && v.id.toLowerCase() === vendorName.toLowerCase())
                );
                if (foundVendor) break;
              }
            }

            if (foundVendor) {
              vendorsObj[vendorName] = foundVendor.logo || null;
            } else {
              vendorsObj[vendorName] = null;
              console.warn(`Vendor not found in vendors_* collections: ${vendorName}`);
            }
          });

          // Update cache with new vendors data
          Cache.setData({
            ...cachedData,
            vendorsData: vendorsObj,
          });
        }

        setWeeklyAds(filteredWeeklyAds);
        setVendorsData(vendorsObj);
      } catch (error) {
        console.error("Error loading weekly ads:", error);
      } finally {
        setLoading(false);
      }
    }

    loadWeeklyAdsWithCache();
  }, []);

  const totalPages = Math.ceil(weeklyAds.length / PAGE_SIZE);
  const paginatedAds = weeklyAds.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6 text-center text-indigo-700 drop-shadow-md tracking-wide">Weekly Ads</h1>

      {loading ? (
        <p className="text-center">Loading weekly ads...</p>
      ) : weeklyAds.length === 0 ? (
        <p className="text-center text-gray-600">No weekly ads found.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
            {paginatedAds.map((ad) => {
              const logo = vendorsData[ad.vendor];
              return (
                <a
                  key={ad.id}
                  href={ad.offerLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border rounded-lg p-4 shadow hover:shadow-lg transition flex flex-col items-center cursor-pointer"
                  title={`${ad.vendor} Weekly Ad`}
                >
                  <div
                    className="w-24 h-24 flex items-center justify-center rounded mb-2 p-2"
                    style={{
                      background: "rgba(200, 200, 200, 0.85)",
                      boxShadow: "0 0 8px rgba(0,0,0,0.25)",
                      border: "1px solid #999",
                    }}
                  >
                    {logo ? (
                      <img
                        src={logo}
                        alt={`${ad.vendor} logo`}
                        className="max-w-full max-h-full object-contain drop-shadow-md"
                        style={{ filter: "drop-shadow(0 0 1px rgba(0,0,0,0.2))" }}
                      />
                    ) : (
                      <div className="text-xs text-gray-400 text-center">No logo</div>
                    )}
                  </div>

                  <div className="text-center font-semibold text-sm truncate max-w-full">
                    {ad.vendor}
                  </div>

                  {ad.description && (
                    <div className="mt-1 text-xs font-bold text-teal-600 text-center max-w-full">
                      {ad.description}
                    </div>
                  )}
                </a>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex justify-center space-x-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >
                Prev
              </button>
              <span className="px-3 py-1 rounded border bg-gray-100">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
